import { useState, useEffect, useMemo, useRef } from 'react';
import { useTheme } from '../styles/ThemeContext';
import { uid } from '../utils/helpers';
import { validateDGMix } from '../utils/dgValidation';
import { apiFetch } from '../utils/api';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { GuidelinesModal } from './modals/GuidelinesModal';
import { UserGuideModal } from './modals/UserGuideModal';
import { MyOrdersPanel } from './modals/MyOrdersPanel';
import { FiBook, FiFileText, FiChevronDown, FiSun, FiMoon } from 'react-icons/fi';
import { ProductSelectionModal } from './modals/ProductSelectionModal';
import { DGWarningModal } from './modals/DGWarningModal';
import { EProductQtyModal } from './modals/EProductQtyModal';
import { Step1Addresses } from './steps/Step1Addresses';
import { Step2ShippingUnits } from './steps/Step2ShippingUnits';
import { Step3Products } from './steps/Step3Products';
import { Step4Preview } from './steps/Step4Preview';
import { Step5Confirmation } from './steps/Step5Confirmation';

/**
 * Main Packing App Component
 * Manages step navigation and all packing list state
 */
export function PackingApp({
  user,
  onLogout,
  defaultProducts = [],
  defaultUnitTypes = [],
  defaultCountries = [],
  defaultGuidelines = [],
  defaultSavedAddresses = [],
  defaultCustomProducts = []
}) {
  const { theme, mode, toggleMode } = useTheme();
  const c = theme.colors;

  // Data state
  const [products, setProducts] = useState(defaultProducts);
  const [unitTypes, setUnitTypes] = useState(defaultUnitTypes);
  const [countries, setCountries] = useState(defaultCountries);
  const [guidelinesData, setGuidelinesData] = useState(defaultGuidelines);
  const [savedAddresses, setSavedAddresses] = useState(defaultSavedAddresses);
  const [selectedAddrId, setSelectedAddrId] = useState({ coll: null, del: null });

  const isAdmin = user && user.role === 'admin';

  // Modal state
  const [guidelinesOpen, setGuidelinesOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [myOrdersOpen, setMyOrdersOpen] = useState(false);
  const [guidesDropdown, setGuidesDropdown] = useState(false);
  const guidesRef = useRef(null);
  useEffect(() => {
    if (!guidesDropdown) return;
    const close = (e) => { if (guidesRef.current && !guidesRef.current.contains(e.target)) setGuidesDropdown(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [guidesDropdown]);
  const [modal, setModal] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [dgWarning, setDgWarning] = useState({
    isOpen: false,
    details: {},
    pendingProds: []
  });
  const [eProductModal, setEProductModal] = useState({
    isOpen: false,
    product: null,
    existingProd: null,
    pendingProds: [],
    skipDgCheck: false
  });

  // Step state
  const [step, setStep] = useState(1);

  // Shipment data
  const [sd, setSd] = useState({
    deptNumber: '',
    projectNumber: '',
    collCompany: '',
    collAddress: '',
    collPostal: '',
    collCity: '',
    collCountry: 'NL',
    collContact: '',
    collPhone: '',
    collEmail: '',
    delCompany: '',
    delAddress: '',
    delPostal: '',
    delCity: '',
    delCountry: '',
    delContact: '',
    delPhone: '',
    delEmail: '',
    pickupDate: '',
    deliveryDate: '',
    preparedBy: ''
  });

  // Shipping units
  const [units, setUnits] = useState([]);
  const [editingUnitId, setEditingUnitId] = useState(null);
  const [newUnit, setNewUnit] = useState({
    type: '',
    number: '1',
    length: '',
    width: '',
    height: '',
    tare: '',
    totalWeight: '',
    export_type: 'Temporary'
  });

  // Products
  const [prods, setProds] = useState([]);
  const [customProducts, setCustomProducts] = useState(defaultCustomProducts); // server-backed custom products

  // Confirmation state
  const [orderNumber, setOrderNumber] = useState('');
  const [refLoading, setRefLoading] = useState(false);
  const [confirmSaved, setConfirmSaved] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmErr, setConfirmErr] = useState('');
  const [draftId, setDraftId] = useState(null);
  const [draftSaveLoading, setDraftSaveLoading] = useState(false);
  const [draftSaveMsg, setDraftSaveMsg] = useState('');
  const [orderSubmittedModal, setOrderSubmittedModal] = useState(false);

  // Initialize new unit with first unit type
  useEffect(() => {
    if (unitTypes.length > 0 && !newUnit.type) {
      const d = unitTypes[0];
      setNewUnit({
        type: d.type,
        number: '1',
        length: String(d.l || ''),
        width: String(d.w || ''),
        height: String(d.h || ''),
        tare: String(d.tare || ''),
        totalWeight: '',
        export_type: 'Temporary'
      });
    }
  }, [unitTypes, newUnit.type]);

  // Update shipment data field
  const updateField = (field, value) => {
    setSd((prev) => ({ ...prev, [field]: value }));
  };

  // Address helpers
  const clearAddress = (prefix) => {
    setSelectedAddrId((prev) => ({ ...prev, [prefix]: null }));
    setSd((p) => ({
      ...p,
      [prefix + 'Company']: '',
      [prefix + 'Address']: '',
      [prefix + 'Postal']: '',
      [prefix + 'City']: '',
      [prefix + 'Country']: '',
      [prefix + 'Contact']: '',
      [prefix + 'Phone']: '',
      [prefix + 'Email']: ''
    }));
  };

  const selectAddress = (addrId, prefix) => {
    const a = savedAddresses.find((x) => x.id === Number(addrId));
    if (!a) return;
    setSelectedAddrId((prev) => ({ ...prev, [prefix]: Number(addrId) }));
    setSd((p) => ({
      ...p,
      [prefix + 'Company']: a.company || '',
      [prefix + 'Address']: a.address || '',
      [prefix + 'Postal']: a.postal || '',
      [prefix + 'City']: a.city || '',
      [prefix + 'Country']: a.country || '',
      [prefix + 'Contact']: a.contact || '',
      [prefix + 'Phone']: a.phone || '',
      [prefix + 'Email']: a.email || ''
    }));
  };

  // Address save confirmation modal state
  const [addrConfirm, setAddrConfirm] = useState(null); // { prefix, addr, existingId, company, changes }

  const ADDR_LABELS = { company: 'Company', address: 'Address', postal: 'Postal Code', city: 'City', country: 'Country', contact: 'Contact', phone: 'Phone', email: 'Email' };

  const saveAddress = (prefix) => {
    const addr = {
      company: sd[prefix + 'Company'] || '',
      address: sd[prefix + 'Address'] || '',
      postal: sd[prefix + 'Postal'] || '',
      city: sd[prefix + 'City'] || '',
      country: sd[prefix + 'Country'] || '',
      contact: sd[prefix + 'Contact'] || '',
      phone: sd[prefix + 'Phone'] || '',
      email: sd[prefix + 'Email'] || ''
    };
    if (!addr.company.trim()) {
      alert('Company name is required to save an address.');
      return;
    }
    const existingId = selectedAddrId[prefix];
    let changes = [];
    if (existingId) {
      const orig = savedAddresses.find((x) => x.id === existingId);
      if (orig) {
        Object.keys(ADDR_LABELS).forEach((key) => {
          const oldVal = (orig[key] || '').trim();
          const newVal = (addr[key] || '').trim();
          if (oldVal !== newVal) {
            changes.push({ field: ADDR_LABELS[key], oldVal: oldVal || '(empty)', newVal: newVal || '(empty)' });
          }
        });
        if (changes.length === 0) {
          alert('No changes detected.');
          return;
        }
      }
    }
    setAddrConfirm({ prefix, addr, existingId, company: addr.company, changes });
  };

  const confirmSaveAddress = async () => {
    if (!addrConfirm) return;
    const { prefix, addr, existingId } = addrConfirm;
    setAddrConfirm(null);
    if (existingId) {
      try {
        const res = await apiFetch('/api/addresses/' + existingId, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(addr)
        });
        const data = await res.json();
        if (res.ok) {
          setSavedAddresses((prev) => prev.map((x) => x.id === existingId ? data : x));
          setSelectedAddrId((prev) => ({ ...prev, [prefix]: null }));
        } else alert(data.error || 'Update failed');
      } catch (e) {
        alert('Update failed');
      }
    } else {
      try {
        const res = await apiFetch('/api/addresses', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(addr)
        });
        const data = await res.json();
        if (res.ok) {
          setSavedAddresses((prev) => [...prev, data]);
        } else alert(data.error || 'Save failed');
      } catch (e) {
        alert('Save failed');
      }
    }
  };

  const deleteAddress = async (addrId) => {
    const a = savedAddresses.find((x) => x.id === Number(addrId));
    if (!a || !confirm('Delete saved address "' + a.company + '"?')) return;
    try {
      const res = await apiFetch('/api/addresses/' + addrId, { method: 'DELETE' });
      if (res.ok) {
        setSavedAddresses((prev) => prev.filter((x) => x.id !== Number(addrId)));
        // Reset selectedAddrId if the deleted address was selected
        setSelectedAddrId((prev) => ({
          coll: prev.coll === Number(addrId) ? null : prev.coll,
          del: prev.del === Number(addrId) ? null : prev.del
        }));
      }
    } catch (e) {
      alert('Delete failed');
    }
  };

  // Unit helpers
  const handleUnitTypeChange = (type) => {
    const d = unitTypes.find((u) => u.type === type);
    if (d)
      setNewUnit((p) => ({
        ...p,
        type: type,
        length: String(d.l || ''),
        width: String(d.w || ''),
        height: String(d.h || ''),
        tare: String(d.tare || '')
      }));
  };

  const updateNewUnit = (field, value) => {
    setNewUnit((p) => ({ ...p, [field]: value }));
  };

  const addShipUnit = () => {
    setUnits((p) => [...p, { ...newUnit, id: uid() }]);
    const d = unitTypes[0] || { type: '', tare: 0, l: 0, w: 0, h: 0 };
    setNewUnit({
      type: d.type,
      number: String(units.length + 2),
      length: String(d.l),
      width: String(d.w),
      height: String(d.h),
      tare: String(d.tare),
      totalWeight: '',
      export_type: 'Temporary'
    });
  };

  const removeUnit = (id) => {
    setUnits((p) => p.filter((u) => u.id !== id));
    setProds((p) => p.filter((x) => x.unitId !== id));
  };

  const editUnit = (id) => {
    const unit = units.find((u) => u.id === id);
    if (unit) {
      setNewUnit({
        type: unit.type,
        number: unit.number,
        length: unit.length,
        width: unit.width,
        height: unit.height,
        tare: unit.tare,
        totalWeight: unit.totalWeight || '',
        export_type: unit.export_type || 'Temporary'
      });
      setEditingUnitId(id);
    }
  };

  const updateUnit = () => {
    setUnits((p) =>
      p.map((u) =>
        u.id === editingUnitId
          ? { ...newUnit, id: u.id }
          : u
      )
    );
    cancelEditUnit();
  };

  const cancelEditUnit = () => {
    setEditingUnitId(null);
    const d = unitTypes[0] || { type: '', tare: 0, l: 0, w: 0, h: 0 };
    setNewUnit({
      type: d.type,
      number: String(units.length + 1),
      length: String(d.l),
      width: String(d.w),
      height: String(d.h),
      tare: String(d.tare),
      totalWeight: '',
      export_type: 'Temporary'
    });
  };

  // Product modal helpers
  const openProductModal = (unitId) => {
    setSelectedUnit(unitId);
    setModal(true);
  };

  const handleProductsConfirm = (selectedProds, skipDgCheck = false) => {
    if (selectedProds.length === 0) return;

    // Get existing products in this unit
    const existingInUnit = prods.filter(p => p.unitId === selectedUnit);

    // Validate DG mix (skip when coming from DG modal confirm)
    if (!skipDgCheck) {
      const validation = validateDGMix(existingInUnit, selectedProds);
      if (validation.hasConflict) {
        setDgWarning({
          isOpen: true,
          details: validation.details,
          pendingProds: selectedProds
        });
        return;
      }
    }

    // Check for product type "E" already in unit - show quantity modal
    const productType = (p) => (p.product?.product_type || p.product?.productType || 'U').toString().trim().toUpperCase();
    const matchE = selectedProds.find((s) => {
      if (productType(s) !== 'E') return false;
      return existingInUnit.some(
        (p) => p.product === s.product.product && p.category === s.product.category
      );
    });
    if (matchE) {
      const existingProd = existingInUnit.find(
        (p) => p.product === matchE.product.product && p.category === matchE.product.category
      );
      setEProductModal({
        isOpen: true,
        product: matchE,
        existingProd,
        pendingProds: selectedProds,
        skipDgCheck
      });
      return;
    }

    // No conflict - proceed with adding products
    addProductsToUnit(selectedProds);
  };

  // Helper to actually add products (used by handleProductsConfirm and DG modal confirm)
  const addProductsToUnit = (selectedProds) => {
    const u = units.find((x) => x.id === selectedUnit);
    const newItems = selectedProds.map((s) => ({
      id: uid(),
      unitId: selectedUnit,
      unitLabel: `${u?.type} ${u?.number}`,
      category: s.product.category,
      product: s.product.product,
      productType: s.product.product_type || 'U',
      qty: s.qty,
      eqNumber: s.eq,
      weight: s.wt,
      coo: s.product.coo || '',
      hsCode: s.product.hs_code || '',
      itemPrice: s.product.value || 0,
      dg: s.product.dg || 'No',
      exportType: s.product.export_type || '',
      manufacturer: s.product.manufacturer || '',
      dimensions_length: s.product.dimensions_length || 0,
      dimensions_width: s.product.dimensions_width || 0,
      dimensions_height: s.product.dimensions_height || 0
    }));
    setProds((p) => [...p, ...newItems]);
    setModal(false);
  };

  // DG Warning Modal handlers
  const handleDgWarningConfirm = () => {
    const pending = dgWarning.pendingProds;
    setDgWarning({ isOpen: false, details: {}, pendingProds: [] });
    handleProductsConfirm(pending, true); // skip DG check, but run E check
  };

  const handleDgWarningCancel = () => {
    setDgWarning({ isOpen: false, details: {}, pendingProds: [] });
  };

  const handleEProductConfirm = (addQty) => {
    const { existingProd, pendingProds, skipDgCheck } = eProductModal;
    const newQty = Number(existingProd.qty) + Number(addQty);
    updateProduct(existingProd.id, { qty: newQty });
    const rest = pendingProds.filter(
      (s) => !(s.product.product === existingProd.product && s.product.category === existingProd.category)
    );
    setEProductModal({ isOpen: false, product: null, existingProd: null, pendingProds: [], skipDgCheck: false });
    if (rest.length > 0) handleProductsConfirm(rest, skipDgCheck);
    else setModal(false);
  };

  const handleEProductCancel = () => {
    const { existingProd, pendingProds, skipDgCheck } = eProductModal;
    const rest = pendingProds.filter(
      (s) => !(s.product.product === existingProd.product && s.product.category === existingProd.category)
    );
    setEProductModal({ isOpen: false, product: null, existingProd: null, pendingProds: [], skipDgCheck: false });
    if (rest.length > 0) handleProductsConfirm(rest, skipDgCheck);
    else setModal(false);
  };

  const removeProduct = (id) => {
    setProds((p) => p.filter((x) => x.id !== id));
  };

  const updateProduct = (id, updatedFields) => {
    setProds((p) =>
      p.map((x) => (x.id === id ? { ...x, ...updatedFields } : x))
    );
  };

  // Calculations
  const totalValue = prods.reduce((s, p) => s + p.itemPrice * p.qty, 0);
  const totalQty = prods.reduce((s, p) => s + Number(p.qty), 0);

  // Order number generation
  const generateOrderNumber = async () => {
    setRefLoading(true);
    try {
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      const dateStr = dd + mm + yyyy;
      const res = await apiFetch('/api/orders/next-number?date=' + dateStr);
      const data = await res.json();
      if (res.ok && data.order_number) setOrderNumber(data.order_number);
      else setOrderNumber('PL-0001-' + dateStr);
    } catch (e) {
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      setOrderNumber('PL-0001-' + dd + mm + yyyy);
    }
    setRefLoading(false);
  };

  const goToPreview = async () => {
    await generateOrderNumber();
    setStep(4);
  };

  // Save draft (work in progress)
  const handleSaveDraft = async () => {
    setDraftSaveMsg('');
    setDraftSaveLoading(true);
    try {
      const payload = {
        order_number: orderNumber,
        project_number: sd.projectNumber || '',
        dept_number: sd.deptNumber || '',
        import_type: 'Temporary',
        addresses: sd,
        units: units,
        products: prods,
        total_units: units.length,
        total_products: prods.length,
        total_qty: totalQty,
        total_value: totalValue,
        has_dg: prods.some((p) => p.dg === 'Yes'),
        created_by: user ? user.username : '',
        status: 'draft',
        current_step: step
      };
      if (draftId) {
        const res = await apiFetch('/api/orders/' + draftId, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, status: 'draft' })
        });
        const data = await res.json();
        if (!res.ok) {
          setDraftSaveMsg(data.error || 'Update failed');
          setDraftSaveLoading(false);
          return;
        }
      } else {
        let ordNum = orderNumber;
        if (!ordNum) {
          const now = new Date();
          const dd = String(now.getDate()).padStart(2, '0');
          const mm = String(now.getMonth() + 1).padStart(2, '0');
          const yyyy = now.getFullYear();
          const dateStr = dd + mm + yyyy;
          const numRes = await apiFetch('/api/orders/next-number?date=' + dateStr);
          const numData = await numRes.json();
          ordNum = numRes.ok && numData.order_number ? numData.order_number : 'PL-0001-' + dateStr;
          setOrderNumber(ordNum);
        }
        const res = await apiFetch('/api/orders', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, order_number: ordNum })
        });
        const data = await res.json();
        if (!res.ok) {
          setDraftSaveMsg(data.error || 'Save failed');
          setDraftSaveLoading(false);
          return;
        }
        setDraftId(data.id);
        setOrderNumber(data.order_number);
      }
      setDraftSaveMsg('Draft saved');
      setTimeout(() => setDraftSaveMsg(''), 2500);
    } catch (err) {
      setDraftSaveMsg('Connection failed');
    }
    setDraftSaveLoading(false);
  };

  // Load draft from My Orders
  const handleLoadDraft = (draftData) => {
    if (!draftData) return;
    const a = draftData.addresses || {};
    setSd({
      deptNumber: a.deptNumber || draftData.dept_number || '',
      projectNumber: a.projectNumber || draftData.project_number || '',
      collCompany: a.collCompany || '',
      collAddress: a.collAddress || '',
      collPostal: a.collPostal || '',
      collCity: a.collCity || '',
      collCountry: a.collCountry || 'NL',
      collContact: a.collContact || '',
      collPhone: a.collPhone || '',
      collEmail: a.collEmail || '',
      delCompany: a.delCompany || '',
      delAddress: a.delAddress || '',
      delPostal: a.delPostal || '',
      delCity: a.delCity || '',
      delCountry: a.delCountry || 'NL',
      delContact: a.delContact || '',
      delPhone: a.delPhone || '',
      delEmail: a.delEmail || '',
      pickupDate: a.pickupDate || '',
      deliveryDate: a.deliveryDate || '',
      preparedBy: a.preparedBy || ''
    });
    setUnits(draftData.units || []);
    setProds(draftData.products || []);
    setOrderNumber(draftData.order_number || '');
    setDraftId(draftData.id);
    setConfirmSaved(null);
    setStep(draftData.current_step || 1);
    setMyOrdersOpen(false);
  };

  // Confirmation save
  const handleConfirmSave = async () => {
    setConfirmErr('');
    setConfirmLoading(true);
    try {
      const payload = {
        order_number: orderNumber,
        project_number: sd.projectNumber || '',
        dept_number: sd.deptNumber || '',
        import_type: 'Temporary',
        addresses: sd,
        units: units,
        products: prods,
        total_units: units.length,
        total_products: prods.length,
        total_qty: totalQty,
        total_value: totalValue,
        has_dg: prods.some((p) => p.dg === 'Yes'),
        created_by: user ? user.username : ''
      };
      if (draftId) {
        const res = await apiFetch('/api/orders/' + draftId, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, status: 'confirmed' })
        });
        const data = await res.json();
        if (!res.ok) {
          setConfirmErr(data.error || 'Confirm failed');
          setConfirmLoading(false);
          return;
        }
        setConfirmSaved(data.order_number);
        setDraftId(null);
      } else {
        const res = await apiFetch('/api/orders', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (!res.ok) {
          setConfirmErr(data.error || 'Save failed');
          setConfirmLoading(false);
          return;
        }
        setConfirmSaved(data.order_number);
      }
    } catch (err) {
      setConfirmErr('Connection failed. Check if the server is running.');
    }
    setConfirmLoading(false);
  };

  const handleNewPackingList = () => {
    setStep(1);
    setSd({
      deptNumber: '',
      projectNumber: '',
      collCompany: '',
      collAddress: '',
      collPostal: '',
      collCity: '',
      collCountry: 'NL',
      collContact: '',
      collPhone: '',
      collEmail: '',
      delCompany: '',
      delAddress: '',
      delPostal: '',
      delCity: '',
      delCountry: '',
      delContact: '',
      delPhone: '',
      delEmail: '',
      pickupDate: '',
      deliveryDate: '',
      preparedBy: ''
    });
    setUnits([]);
    setProds([]);
    setConfirmSaved(null);
    setOrderNumber('');
    setDraftId(null);
  };

  const handlePrint = () => {
    setStep(4);
    setTimeout(() => window.print(), 300);
  };

  // Step 1 required fields validation
  const STEP1_REQUIRED = [
    'collCompany', 'collAddress', 'collCity', 'collCountry', 'collContact',
    'delCompany', 'delAddress', 'delCity', 'delCountry', 'delContact',
    'preparedBy'
  ];
  const step1Valid = STEP1_REQUIRED.every((f) => sd[f]?.trim());
  const [step1Error, setStep1Error] = useState(false);

  const navigateStep = (targetStep) => {
    // If order is already saved, prevent going back to edit - show modal instead
    if (confirmSaved && targetStep < 5) {
      setOrderSubmittedModal(true);
      return;
    }
    // If currently on step 1 and trying to go forward, validate
    if (step === 1 && targetStep > 1 && !step1Valid) {
      setStep1Error(true);
      return;
    }
    setStep1Error(false);
    setStep(targetStep);
  };

  const STEPS = [
    { n: 1, l: '1. Addresses' },
    { n: 2, l: '2. Shipping Units' },
    { n: 3, l: '3. Products' },
    { n: 4, l: '4. Preview' },
    { n: 5, l: '5. Confirm' }
  ];

  return (
    <div
      style={{
        minHeight: '100vh',
        background: c.bg,
        color: c.text,
        fontFamily: "'Segoe UI',sans-serif",
        fontSize: 13
      }}
    >
      {/* Header */}
      <div
        style={{
          background: c.bgCard,
          borderBottom: '1px solid ' + c.border,
          padding: '12px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}
      >
        <div
          style={{
            background: c.accent,
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 12,
            color: '#000',
            borderRadius: 2,
            flexShrink: 0
          }}
        >
          TMS
        </div>
        <div>
          <div style={{ fontWeight: 600 }}>Packing List Generator</div>
          <div
            style={{
              fontSize: 10,
              color: c.textSecondary,
              letterSpacing: 1,
              fontFamily: 'monospace'
            }}
          >
            TRANSPORT MANAGEMENT SYSTEM
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, alignItems: 'center' }}>
          <div
            onClick={toggleMode}
            title={mode === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              background: mode === 'dark' ? '#111' : '#d5d5d5',
              borderRadius: 50,
              padding: 2,
              width: 76,
              height: 26,
              position: 'relative',
              transition: 'background 0.3s'
            }}
          >
            <div style={{
              position: 'absolute',
              left: mode === 'dark' ? 2 : 'calc(100% - 24px)',
              width: 22, height: 22,
              borderRadius: '50%',
              background: '#fff',
              border: '1px solid ' + (mode === 'dark' ? '#444' : '#bbb'),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'left 0.3s',
              boxShadow: '0 1px 3px rgba(0,0,0,.2)'
            }}>
              {mode === 'dark' ? <FiMoon size={11} color="#111" /> : <FiSun size={11} color="#333" />}
            </div>
            <span style={{
              flex: 1,
              textAlign: mode === 'dark' ? 'right' : 'left',
              fontSize: 5.5,
              fontWeight: 800,
              letterSpacing: 0.6,
              color: mode === 'dark' ? '#fff' : '#444',
              paddingLeft: mode === 'light' ? 7 : 0,
              paddingRight: mode === 'dark' ? 7 : 0,
              userSelect: 'none'
            }}>
              {mode === 'dark' ? 'NIGHT MODE' : 'DAY MODE'}
            </span>
          </div>
          <div ref={guidesRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setGuidesDropdown(p => !p)}
              style={{ ...theme.btnG, padding: '6px 12px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 5 }}
            >
              <FiBook size={13} /> Guides <FiChevronDown size={11} style={{ transition: 'transform .2s', transform: guidesDropdown ? 'rotate(180deg)' : 'rotate(0)' }} />
            </button>
            {guidesDropdown && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 4px)', left: 0, minWidth: 170,
                background: c.bgCard, border: '1px solid ' + c.border, borderRadius: 4,
                boxShadow: '0 8px 24px ' + c.shadow, zIndex: 150, overflow: 'hidden'
              }}>
                <div
                  onClick={() => { setGuidelinesOpen(true); setGuidesDropdown(false); }}
                  style={{ padding: '9px 14px', fontSize: 12, color: c.text, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid ' + c.border }}
                  onMouseEnter={e => e.currentTarget.style.background = c.bgInput}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  📋 Guidelines
                </div>
                <div
                  onClick={() => { setGuideOpen(true); setGuidesDropdown(false); }}
                  style={{ padding: '9px 14px', fontSize: 12, color: c.text, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                  onMouseEnter={e => e.currentTarget.style.background = c.bgInput}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <FiBook size={13} /> User Guide
                </div>
              </div>
            )}
          </div>
          {STEPS.map((st) => (
            <div
              key={st.n}
              onClick={() => (st.n <= step || st.n === step + 1) && navigateStep(st.n)}
              style={{
                padding: '7px 14px',
                cursor: 'pointer',
                background:
                  step === st.n
                    ? c.accentBg
                    : step > st.n
                    ? c.successBg
                    : 'transparent',
                border: '1px solid',
                borderColor:
                  step === st.n
                    ? c.accentBorder
                    : step > st.n
                    ? c.successBorder
                    : c.border,
                borderRadius: 3,
                fontSize: 11,
                color: step >= st.n ? c.text : c.textSecondary,
                fontWeight: step === st.n ? 600 : 400
              }}
            >
              {step > st.n ? '✓ ' + st.l.split('. ')[1] : st.l}
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1px solid ' + c.accent, borderRadius: 4, padding: '4px 10px', marginLeft: 6 }}>
            {user && (
              <span style={{ fontSize: 11, color: c.textSecondary, display: 'flex', alignItems: 'center', gap: 4 }}>
                User: <strong style={{ color: c.text, fontFamily: 'monospace' }}>{user.username}</strong>
              </span>
            )}
            <div style={{ width: 1, height: 18, background: c.border }} />
            <button
              onClick={() => setMyOrdersOpen(true)}
              style={{ ...theme.btnG, padding: '5px 10px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 5, border: 'none' }}
            >
              <FiFileText size={13} /> My Orders
            </button>
            {step >= 1 && !confirmSaved && (
              <>
                <div style={{ width: 1, height: 18, background: c.border }} />
                <button
                  onClick={handleSaveDraft}
                  disabled={draftSaveLoading}
                  style={{ ...theme.btnG, padding: '5px 10px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 5, borderColor: c.accent, color: c.accent, opacity: draftSaveLoading ? 0.7 : 1 }}
                >
                  {draftSaveLoading ? 'Saving...' : 'Save draft'}
                </button>
                {draftSaveMsg && <span style={{ fontSize: 11, color: c.success }}>{draftSaveMsg}</span>}
              </>
            )}
          </div>
          {isAdmin && (
            <a
              href="/admin.html"
              style={{
                padding: '7px 14px',
                cursor: 'pointer',
                background: 'transparent',
                border: '1px solid ' + c.border,
                borderRadius: 3,
                fontSize: 11,
                color: c.textSecondary,
                fontFamily: 'inherit',
                textDecoration: 'none',
                display: 'inline-block'
              }}
            >
              Admin Portal
            </a>
          )}
          <button
            onClick={onLogout}
            style={{
              padding: '7px 14px',
              cursor: 'pointer',
              background: 'transparent',
              border: '1px solid ' + c.border,
              borderRadius: 3,
              fontSize: 11,
              color: c.textSecondary,
              fontFamily: 'inherit'
            }}
          >
            Log Out
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 20px' }}>
        {/* Step 1 */}
        {step === 1 && (
          <Step1Addresses
            sd={sd}
            onUpdate={updateField}
            countries={countries}
            savedAddresses={savedAddresses}
            selectedAddrId={selectedAddrId}
            onSelectAddress={selectAddress}
            onSaveAddress={saveAddress}
            onClearAddress={clearAddress}
            onDeleteAddress={deleteAddress}
            onNext={() => navigateStep(2)}
            externalError={step1Error}
            onClearExternalError={() => setStep1Error(false)}
            user={user}
          />
        )}

        {/* Step 2 */}
        {step === 2 && (
          <Step2ShippingUnits
            unitTypes={unitTypes}
            units={units}
            newUnit={newUnit}
            onNewUnitChange={updateNewUnit}
            onUnitTypeChange={handleUnitTypeChange}
            onAddUnit={addShipUnit}
            onUpdateUnit={updateUnit}
            onEditUnit={editUnit}
            onCancelEdit={cancelEditUnit}
            onRemoveUnit={removeUnit}
            editingUnitId={editingUnitId}
            products={prods}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
          />
        )}

        {/* Step 3 */}
        {step === 3 && (
          <Step3Products
            units={units}
            products={prods}
            onOpenProductModal={openProductModal}
            onRemoveProduct={removeProduct}
            onUpdateProduct={updateProduct}
            totalQty={totalQty}
            totalValue={totalValue}
            onBack={() => setStep(2)}
            onNext={goToPreview}
          />
        )}

        {/* Step 4 */}
        {step === 4 && (
          <Step4Preview
            sd={sd}
            units={units}
            products={prods}
            orderNumber={orderNumber}
            totalQty={totalQty}
            totalValue={totalValue}
            onBack={() => setStep(3)}
            onPrint={() => window.print()}
            onNext={() => setStep(5)}
          />
        )}

        {/* Step 5 */}
        {step === 5 && (
          <Step5Confirmation
            sd={sd}
            units={units}
            products={prods}
            orderNumber={orderNumber}
            totalQty={totalQty}
            totalValue={totalValue}
            confirmSaved={confirmSaved}
            confirmLoading={confirmLoading}
            confirmError={confirmErr}
            onConfirmSave={handleConfirmSave}
            onSaveDraft={handleSaveDraft}
            draftSaveLoading={draftSaveLoading}
            onPrint={handlePrint}
            onNewPackingList={handleNewPackingList}
            onBack={() => setStep(4)}
            onViewMyOrders={() => setMyOrdersOpen(true)}
          />
        )}
      </div>

      {/* Product Selection Modal */}
      <ProductSelectionModal
        isOpen={modal}
        onClose={() => setModal(false)}
        products={[...products, ...customProducts]}
        onConfirm={handleProductsConfirm}
        onSaveCustomProduct={async (prod) => {
          // Save to server database
          try {
            const res = await apiFetch('/api/custom-products', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                category: prod.category,
                product: prod.product,
                coo: prod.coo,
                hs_code: prod.hs_code,
                value: prod.value,
                dg: prod.dg,
                export_type: prod.export_type
              })
            });
            if (res.ok) {
              const saved = await res.json();
              setCustomProducts((prev) => [...prev, saved]);
            } else {
              // Fallback to session-only if save fails
              setCustomProducts((prev) => {
                if (prev.some((p) => p.product === prod.product)) return prev;
                return [...prev, prod];
              });
            }
          } catch {
            // Fallback to session-only if server unreachable
            setCustomProducts((prev) => {
              if (prev.some((p) => p.product === prod.product)) return prev;
              return [...prev, prod];
            });
          }
        }}
        onEditCustomProduct={async (prod) => {
          try {
            const res = await apiFetch('/api/custom-products/' + prod.id, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                category: prod.category,
                product: prod.product,
                coo: prod.coo,
                hs_code: prod.hs_code,
                value: prod.value,
                dg: prod.dg,
                export_type: prod.export_type
              })
            });
            if (res.ok) {
              const saved = await res.json();
              setCustomProducts((prev) => prev.map((p) => p.id === saved.id ? saved : p));
            }
          } catch {
            // silently fail
          }
        }}
        onDeleteCustomProduct={async (prod) => {
          try {
            const res = await apiFetch('/api/custom-products/' + prod.id, {
              method: 'DELETE'
            });
            if (res.ok || res.status === 204) {
              setCustomProducts((prev) => prev.filter((p) => p.id !== prod.id));
            }
          } catch {
            // silently fail
          }
        }}
        unitLabel={
          selectedUnit
            ? `${units.find((u) => u.id === selectedUnit)?.type} ${
                units.find((u) => u.id === selectedUnit)?.number
              }`
            : ''
        }
      />

      {/* Guidelines Modal */}
      <GuidelinesModal
        isOpen={guidelinesOpen}
        onClose={() => setGuidelinesOpen(false)}
        guidelinesData={guidelinesData}
      />

      {/* User Guide Modal */}
      <UserGuideModal
        isOpen={guideOpen}
        onClose={() => setGuideOpen(false)}
      />

      {/* My Orders Panel */}
      <MyOrdersPanel
        isOpen={myOrdersOpen}
        onClose={() => setMyOrdersOpen(false)}
        username={user?.username}
        onResumeDraft={handleLoadDraft}
      />

      {/* DG Warning Modal */}
      <DGWarningModal
        isOpen={dgWarning.isOpen}
        onClose={handleDgWarningCancel}
        onConfirm={handleDgWarningConfirm}
        details={dgWarning.details}
      />

      {/* E Product Quantity Modal - when adding type E product already in unit */}
      <EProductQtyModal
        isOpen={eProductModal.isOpen}
        onClose={handleEProductCancel}
        onConfirm={handleEProductConfirm}
        product={eProductModal.product}
        existingProd={eProductModal.existingProd}
      />

      {/* Order submitted - prevent editing after save */}
      {orderSubmittedModal && (
        <div
          onClick={() => setOrderSubmittedModal(false)}
          style={{ position: 'fixed', inset: 0, background: c.overlay, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: c.bgCard, border: '1px solid ' + c.border, borderRadius: 6, padding: 24, maxWidth: 420, width: '100%', boxShadow: '0 16px 32px ' + c.shadow }}
          >
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 12, color: c.text }}>Order submitted</div>
            <p style={{ margin: '0 0 20px 0', fontSize: 13, lineHeight: 1.6, color: c.textSecondary }}>
              The order has been submitted. If you want to make changes or add more items, please create a new packing list.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setOrderSubmittedModal(false)} style={theme.btnG}>Cancel</button>
              <button onClick={() => { setOrderSubmittedModal(false); handleNewPackingList(); }} style={theme.btnP}>+ New Packing List</button>
            </div>
          </div>
        </div>
      )}

      {/* Address Save/Update Confirmation Modal */}
      {addrConfirm && (
        <div
          onClick={() => setAddrConfirm(null)}
          style={{ position: 'fixed', inset: 0, background: c.overlay, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: c.bgCard, border: '1px solid ' + c.border, borderRadius: 4, padding: 24, maxWidth: 480, width: '100%', boxShadow: '0 8px 24px ' + c.shadow }}
          >
            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 12, color: c.text }}>
              {addrConfirm.existingId ? '📋 Update Address' : '💾 Save New Address'}
            </div>
            <div style={{ fontSize: 12, color: c.textSecondary, marginBottom: 12, lineHeight: 1.6 }}>
              {addrConfirm.existingId
                ? 'The following changes will be saved:'
                : 'Save this as a new address in your address book?'}
            </div>
            {addrConfirm.existingId && addrConfirm.changes?.length > 0 ? (
              <div style={{ marginBottom: 16, border: '1px solid ' + c.border, borderRadius: 4, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '6px 10px', background: c.bgInput, color: c.textSecondary, fontFamily: 'monospace', fontSize: 9, letterSpacing: 1 }}>FIELD</th>
                      <th style={{ textAlign: 'left', padding: '6px 10px', background: c.bgInput, color: c.textSecondary, fontFamily: 'monospace', fontSize: 9, letterSpacing: 1 }}>OLD</th>
                      <th style={{ textAlign: 'left', padding: '6px 10px', background: c.bgInput, color: c.textSecondary, fontFamily: 'monospace', fontSize: 9, letterSpacing: 1 }}>NEW</th>
                    </tr>
                  </thead>
                  <tbody>
                    {addrConfirm.changes.map((ch, i) => (
                      <tr key={i}>
                        <td style={{ padding: '5px 10px', borderTop: '1px solid ' + c.border, color: c.text, fontWeight: 500 }}>{ch.field}</td>
                        <td style={{ padding: '5px 10px', borderTop: '1px solid ' + c.border, color: c.error, textDecoration: 'line-through' }}>{ch.oldVal}</td>
                        <td style={{ padding: '5px 10px', borderTop: '1px solid ' + c.border, color: c.success, fontWeight: 600 }}>{ch.newVal}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ fontSize: 13, fontWeight: 600, color: c.accent, marginBottom: 16, fontFamily: 'monospace' }}>
                "{addrConfirm.company}"
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setAddrConfirm(null)} style={{ ...theme.btnG, padding: '8px 16px', fontSize: 12 }}>Cancel</button>
              <button onClick={confirmSaveAddress} style={{ ...theme.btnP, padding: '8px 16px', fontSize: 12 }}>
                {addrConfirm.existingId ? '✓ Update' : '✓ Save'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
